import { useEffect, useState, useMemo } from "react";
import { Skeleton, Dropdown, Drawer } from "../../components/ui";
import DataTableCards from "../../components/DataTable/DataTableCards";
import dayjs from "dayjs";
import swal from "../../utils/swal";
import {
  Search,
  Filter,
  Plus,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  Send,
  Wallet,
  SearchX,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  useDeleteFee,
  useGetAllFees,
  useReviewFee,
} from "../../query/feesQuery";
import { useGetClassrooms } from "../../query/childQuery";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import {
  childName,
  formatAmount,
  monthYearLabel,
  MONTH_NAMES,
  FEE_STATUSES,
  voucherStatus,
  statusLabel,
  statusTone,
  voucherTotal,
  canEditVoucher,
} from "./feeHelpers";
import FeeReceiptNo from "./FeeReceiptNo";
import "../teachers/teachers.css";
import "./fees.css";
import FilterSelect from "../../components/FilterSelect";

function pageWindow(current, totalPages) {
  if (totalPages <= 3) return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (current <= 2) return [1, 2, 3];
  if (current >= totalPages - 1) return [totalPages - 2, totalPages - 1, totalPages];
  return [current - 1, current, current + 1];
}

function Fees() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedClassroom, setSelectedClassroom] = useState("");
  const [paginationConfig, setPaginationConfig] = useState({
    pageNumber: 1,
    limit: "10",
    keyword: "",
    status: "",
    month: "",
    classroom: "",
  });
  const debouncedKeyword = useDebouncedValue(searchTerm.trim(), 400);

  const queryParams = useMemo(
    () => ({
      page: String(paginationConfig.pageNumber),
      limit: String(paginationConfig.limit),
      keyword: paginationConfig.keyword,
      status: paginationConfig.status,
      month: paginationConfig.month,
      classroom: paginationConfig.classroom,
    }),
    [paginationConfig]
  );

  const { isLoading, isFetching, data: feesData } = useGetAllFees(queryParams);
  const { mutate: reviewFee, isPending: isReviewing } = useReviewFee();
  const { mutate: deleteFee } = useDeleteFee();
  const { data: classrooms = [] } = useGetClassrooms();

  const docs = feesData?.docs || [];
  const pageSize = Number(paginationConfig.limit) || 10;
  const currentPage = Number(paginationConfig.pageNumber) || 1;
  const totalDocs = feesData?.totalDocs || 0;
  const totalPages = Math.max(1, Math.ceil(totalDocs / pageSize));
  const from = totalDocs === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalDocs);
  const hasFilters = Boolean(paginationConfig.status || paginationConfig.month || paginationConfig.classroom);

  useEffect(() => {
    setPaginationConfig((prev) =>
      prev.keyword === debouncedKeyword ? prev : { ...prev, keyword: debouncedKeyword, pageNumber: 1 }
    );
  }, [debouncedKeyword]);

  useEffect(() => {
    if (!open) return;
    setSelectedStatus(paginationConfig.status || "");
    setSelectedMonth(paginationConfig.month || "");
    setSelectedClassroom(paginationConfig.classroom || "");
  }, [open, paginationConfig.status, paginationConfig.month, paginationConfig.classroom]);

  const applyFilter = () => {
    setPaginationConfig((prev) => ({
      ...prev,
      pageNumber: 1,
      status: selectedStatus || "",
      month: selectedMonth || "",
      classroom: selectedClassroom || "",
    }));
    setOpen(false);
  };

  const resetFilter = () => {
    setSelectedStatus("");
    setSelectedMonth("");
    setSelectedClassroom("");
    setPaginationConfig((prev) => ({
      ...prev,
      pageNumber: 1,
      status: "",
      month: "",
      classroom: "",
    }));
    setOpen(false);
  };

  const handleMarkPaid = (item) => {
    reviewFee(
      { id: item._id, action: "PAID" },
      {
        onSuccess: () => swal("Success", "Voucher marked paid", "success"),
        onError: (error) => swal("Error", error?.message || "Failed to update status", "error"),
      }
    );
  };

  const handleDelete = (item) => {
    swal({
      title: "Are you sure?",
      text: "Do you want to delete this fee voucher?",
      icon: "warning",
      buttons: ["No, cancel it!", "Yes, I am sure!"],
      dangerMode: true,
    }).then((isConfirm) => {
      if (!isConfirm) return;
      deleteFee(item._id, {
        onSuccess: () => swal("Success", "Fee voucher deleted successfully", "success"),
        onError: (error) => swal("Cannot delete", error?.message || "Failed to delete voucher", "error"),
      });
    });
  };

  return (
    <div className="data-page">
      <div className="data-toolbar">
        <div className="data-toolbar-search">
          <label className="data-search">
            <Search size={16} />
            <input
              placeholder="Search here..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {isFetching && searchTerm ? <span className="data-search-spinner" aria-hidden /> : null}
          </label>
          <button
            type="button"
            className={`data-icon-btn${hasFilters ? " is-active" : ""}`}
            aria-label="Filter"
            onClick={() => setOpen(true)}
          >
            <Filter size={18} />
            {hasFilters ? <span className="data-filter-dot" /> : null}
          </button>
        </div>

        <div className="data-toolbar-actions">
          <button type="button" className="data-secondary" onClick={() => navigate("/fees/send")}>
            <Send size={18} />
            Send month
          </button>
          <button type="button" className="data-primary" onClick={() => navigate("/fees/add")}>
            <Plus size={18} />
            New Voucher
          </button>
        </div>
      </div>

      {feesData?.proofWaiting > 0 ? (
        <button
          type="button"
          className="fee-proof-banner"
          onClick={() =>
            setPaginationConfig((prev) => ({ ...prev, pageNumber: 1, status: "PROOF_SUBMITTED" }))
          }
        >
          {feesData.proofWaiting} payment proof{feesData.proofWaiting === 1 ? "" : "s"} waiting for review
        </button>
      ) : null}

      <div className="data-card">
        {isLoading && !feesData ? (
          <div style={{ padding: 24 }}>
            <Skeleton active />
          </div>
        ) : docs.length === 0 ? (
          <div className="data-empty">
            <span className="data-empty-icon">
              {paginationConfig.keyword || hasFilters ? (
                <SearchX size={36} strokeWidth={1.6} />
              ) : (
                <Wallet size={36} strokeWidth={1.6} />
              )}
            </span>
            <h3>{paginationConfig.keyword || hasFilters ? "No vouchers found" : "No fee vouchers yet"}</h3>
            <p>
              {paginationConfig.keyword || hasFilters
                ? "Try a different keyword or clear filters."
                : "Create a fee voucher to see it listed here."}
            </p>
          </div>
        ) : (
          <>
            <DataTableCards fetching={isFetching || isReviewing}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Receipt No</th>
                    <th>Student</th>
                    <th>Amount</th>
                    <th>Period</th>
                    <th>Due Date</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((item) => {
                    const statusValue = voucherStatus(item);
                    const editable = canEditVoucher(item);
                    const actionItems = [
                      {
                        key: "1",
                        label: <div style={{ padding: "8px 12px" }}>View</div>,
                        onClick: () => navigate(`/fees/${item._id}`),
                      },
                    ];
                    if (editable) {
                      actionItems.push({
                        key: "2",
                        label: <div style={{ padding: "8px 12px" }}>Edit</div>,
                        onClick: () => navigate(`/fees/edit/${item._id}`),
                      });
                    }
                    if (editable && statusValue !== "PAID") {
                      actionItems.push({
                        key: "paid",
                        label: <div style={{ padding: "8px 12px" }}>Mark paid</div>,
                        onClick: () => handleMarkPaid(item),
                      });
                    }
                    if (statusValue !== "PAID" && statusValue !== "WAIVED") {
                      actionItems.push({
                        key: "3",
                        label: <div style={{ padding: "8px 12px", color: "#e83038" }}>Delete</div>,
                        onClick: () => handleDelete(item),
                      });
                    }

                    return (
                      <tr key={item._id}>
                        <td>
                          <FeeReceiptNo value={item.receiptNo} />
                        </td>
                        <td>
                          <p className="data-name">{childName(item.child)}</p>
                        </td>
                        <td>
                          <span className="fee-amount">{formatAmount(voucherTotal(item))}</span>
                        </td>
                        <td>
                          <span className="fee-period">{monthYearLabel(item.month, item.year)}</span>
                        </td>
                        <td>{item.dueDate ? dayjs(item.dueDate).format("MMMM D, YYYY") : "—"}</td>
                        <td>
                          <span className={`fee-status ${statusTone(statusValue)}`}>{statusLabel(statusValue)}</span>
                        </td>
                        <td>
                          <Dropdown menu={{ items: actionItems }} trigger={["click"]} placement="bottomRight">
                            <button type="button" className="data-action" aria-label="More actions">
                              <MoreHorizontal size={20} />
                            </button>
                          </Dropdown>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </DataTableCards>

            <div className="data-pager">
              <p className="data-pager-text">
                Showing {from}-{to} from {totalDocs} data
              </p>
              <div className="data-pages">
                <button
                  type="button"
                  className="data-page-btn is-nav"
                  disabled={currentPage <= 1}
                  onClick={() =>
                    setPaginationConfig((prev) => ({ ...prev, pageNumber: Math.max(1, currentPage - 1) }))
                  }
                >
                  <ChevronLeft size={18} />
                </button>
                {pageWindow(currentPage, totalPages).map((page) => (
                  <button
                    key={page}
                    type="button"
                    className={`data-page-btn${page === currentPage ? " is-active" : ""}`}
                    onClick={() => setPaginationConfig((prev) => ({ ...prev, pageNumber: page }))}
                  >
                    {page}
                  </button>
                ))}
                <button
                  type="button"
                  className="data-page-btn is-nav"
                  disabled={currentPage >= totalPages}
                  onClick={() =>
                    setPaginationConfig((prev) => ({
                      ...prev,
                      pageNumber: Math.min(totalPages, currentPage + 1),
                    }))
                  }
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        placement="right"
        title="Filter"
        width={400}
        footer={
          <>
            <button type="button" className="bw-drawer-btn is-ghost" onClick={resetFilter}>
              Clear All
            </button>
            <button type="button" className="bw-drawer-btn is-primary" onClick={applyFilter}>
              Apply
            </button>
          </>
        }
      >
        <div className="bw-filter-field">
          <p className="bw-filter-label">Status</p>
          <FilterSelect
            multiple
            value={selectedStatus}
            onChange={setSelectedStatus}
            options={[{ value: "", label: "All statuses" }, ...FEE_STATUSES]}
          />
        </div>
        <div className="bw-filter-field">
          <p className="bw-filter-label">Month</p>
          <FilterSelect
            value={selectedMonth}
            onChange={setSelectedMonth}
            options={[
              { value: "", label: "All months" },
              ...MONTH_NAMES.map((label, index) => ({ value: String(index + 1), label })),
            ]}
          />
        </div>
        <div className="bw-filter-field">
          <p className="bw-filter-label">Class</p>
          <FilterSelect
            value={selectedClassroom}
            onChange={setSelectedClassroom}
            options={[
              { value: "", label: "All classes" },
              ...classrooms.map((room) => ({ value: room._id, label: room.classroomName })),
            ]}
          />
        </div>
      </Drawer>
    </div>
  );
}

export default Fees;
